// src/components/AdminLayout.js
import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, Star, LogOut,
  Shield, Zap, Sun, Moon, Sparkles, Briefcase, DollarSign, MessageSquare, Menu,
  ChevronsLeft, ChevronsRight, ScrollText, UserCog, Globe, ChevronDown, CheckSquare, FileText, Clock, LayoutGrid, CalendarDays,
  CreditCard, Megaphone,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { hasPermission } from "../lib/permissions";
import FloatingTimer from "./FloatingTimer";
import packageJson from "../../package.json";
const APP_VERSION = packageJson.version;

const nav = [
  { to: "/admin",          icon: LayoutDashboard, label: "Overview",   end: true, permission: "overview" },
  { to: "/admin/tasks",     icon: CheckSquare,     label: "Tasks",           alwaysVisible: true },
  { to: "/admin/time",      icon: Clock,           label: "Time tracker",    alwaysVisible: true },
  { to: "/admin/bookings",  icon: CalendarDays,    label: "Bookings",        alwaysVisible: true },
  { to: "/admin/leads",      icon: Briefcase,       label: "Leads",           permission: "leads" },
  { to: "/admin/businesses", icon: Users,          label: "Businesses",      permission: "businesses" },
  { to: "/admin/google-posts", icon: Megaphone,    label: "Google Posts",    permission: "google" },
  {
    key: "website", icon: Globe, label: "Website",
    subItems: [
      { to: "/admin/website",         icon: Globe,    label: "Projects", permission: "website" },
      { to: "/admin/website-builder", icon: Sparkles, label: "Builder",    permission: "website" },
      { to: "/admin/blogs",           icon: FileText, label: "Blogs",      permission: "website" },
    ],
  },
  {
    key: "zilo", icon: Sparkles, label: "Zilo AI",
    subItems: [
      { to: "/admin/reviews",    icon: Star,           label: "All Reviews", permission: "reviews" },
      { to: "/admin/campaigns",  icon: MessageSquare,  label: "Campaigns",   permission: "campaigns" },
      { to: "/admin/review-widgets", icon: LayoutGrid, label: "Widgets",     permission: "reviews" },
      { to: "/admin/test-chat",  icon: Sparkles,        label: "Test the AI", permission: "testChat" },
    ],
  },
  { to: "/admin/costs",      icon: DollarSign,      label: "Costs",          superAdminOnly: true },
  { to: "/admin/payments",   icon: CreditCard,      label: "Payments",       superAdminOnly: true },
  { to: "/admin/audit-log",  icon: ScrollText,      label: "Audit Log",      permission: "auditLog" },
];

export default function AdminLayout() {
  const { logout, businessProfile } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("rb-admin-sidebar-collapsed") === "1");
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = new Set();
    if (["/admin/reviews", "/admin/campaigns", "/admin/review-widgets", "/admin/test-chat"].some(p => window.location.pathname.startsWith(p))) initial.add("zilo");
    if (["/admin/website", "/admin/website-builder", "/admin/blogs"].some(p => window.location.pathname.startsWith(p))) initial.add("website");
    return initial;
  });
  // Accordion: opening one group closes the others (clicking the open one closes it).
  function toggleGroup(key) {
    setOpenGroups(prev => (prev.has(key) ? new Set() : new Set([key])));
  }

  function toggleCollapse() {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("rb-admin-sidebar-collapsed", next ? "1" : "0");
      return next;
    });
  }

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const visibleNav = businessProfile
    ? nav
        .map(item => item.subItems
          ? { ...item, subItems: item.subItems.filter(s => hasPermission(businessProfile, s.permission)) }
          : item)
        .filter(item => {
          if (item.subItems) return item.subItems.length > 0;
          if (item.alwaysVisible) return true;
          if (item.superAdminOnly) return !!businessProfile.superAdmin;
          return hasPermission(businessProfile, item.permission);
        })
    : [];

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div className="rb-ambient" aria-hidden="true"><span></span><span></span><span></span></div>
      {/* Admin Sidebar */}
      <aside
        className={`rb-sidebar${mobileOpen ? " rb-sidebar-open" : ""}`}
        style={{
        width: collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)",
        height: "100vh", position: "fixed", left: 0, top: 0, zIndex: 100,
        background: "#0d1220", borderRight: "1px solid rgba(255,255,255,.08)",
        display: "flex", flexDirection: "column",
        transition: "width .2s ease", overflow: "visible",
      }}>
        {/* Collapse toggle — floating circular button straddling the sidebar's right edge */}
        <button
          onClick={toggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            position: "absolute", top: 34, right: -13, zIndex: 101,
            width: 26, height: 26, borderRadius: "50%",
            background: "var(--accent)", border: "3px solid #0d1220",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", cursor: "pointer", transition: "background .15s ease",
            padding: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--accent-dim)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "var(--accent)"; }}
        >
          {collapsed ? <ChevronsRight size={13}/> : <ChevronsLeft size={13}/>}
        </button>

        {/* Logo */}
        <div style={{ padding: collapsed ? "22px 14px 18px" : "22px 20px 18px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start" }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: "linear-gradient(135deg, var(--accent), var(--accent-dim))",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 18px var(--accent-glow)",
            }}>
              <Shield size={18} color="#fff" />
            </div>
            {!collapsed && (
              <div>
                <div className="sidebar-brand-name" style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, letterSpacing: "-.02em" }}>
                  Admin Panel
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,.4)", letterSpacing: ".06em", textTransform: "uppercase" }}>
                  TechBugz
                </div>
              </div>
            )}
            {!collapsed && (
              <button
                onClick={toggleTheme}
                style={{
                  marginLeft: "auto", width: 32, height: 32,
                  background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.13)",
                  borderRadius: "var(--radius)", display: "flex", alignItems: "center", justifyContent: "center",
                  color: "rgba(255,255,255,.5)", cursor: "pointer", flexShrink: 0,
                }}
              >
                {isDark ? <Sun size={14}/> : <Moon size={14}/>}
              </button>
            )}
          </div>
        </div>

        {/* Role badge */}
        <div style={{ padding: collapsed ? "10px 8px" : "10px 14px" }}>
          <div style={{
            background: "var(--accent-lt)", border: "1px solid var(--accent-border)",
            borderRadius: "var(--radius)", padding: collapsed ? "6px" : "6px 12px",
            display: "flex", alignItems: "center", gap: 6, justifyContent: collapsed ? "center" : "flex-start",
          }} title={collapsed ? "Administrator" : undefined}>
            <Shield size={12} color="var(--accent)" />
            {!collapsed && (
              <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 700, letterSpacing: ".04em" }}>
                ADMINISTRATOR
              </span>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: collapsed ? "6px 8px" : "6px 12px", overflowY: "auto", overflowX: "hidden" }}>
          {visibleNav.map(item => {
            if (item.subItems) {
              const Icon = item.icon;
              const groupOpen = openGroups.has(item.key);
              return (
                <div key={item.key}>
                  <button
                    onClick={() => toggleGroup(item.key)}
                    title={collapsed ? item.label : undefined}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      justifyContent: collapsed ? "center" : "flex-start",
                      padding: collapsed ? "10px 0" : "10px 12px", borderRadius: "var(--radius)",
                      fontSize: 14, fontWeight: 500, color: "rgba(255,255,255,.55)",
                      background: "transparent", border: "1px solid transparent", cursor: "pointer",
                      marginBottom: 2, transition: "all .15s", fontFamily: "inherit",
                    }}
                  >
                    <Icon size={16} style={{ flexShrink: 0 }} />
                    {!collapsed && (
                      <>
                        {item.label}
                        <ChevronDown size={12} color="rgba(255,255,255,.35)" style={{ marginLeft: "auto", transform: groupOpen ? "none" : "rotate(-90deg)", transition: "transform .15s" }} />
                      </>
                    )}
                  </button>
                  {(groupOpen || collapsed) && (
                    <div style={{ marginLeft: collapsed ? 0 : 8, paddingLeft: collapsed ? 0 : 8, borderLeft: collapsed ? "none" : "1px solid var(--sidebar-border)" }}>
                      {item.subItems.map(({ to, icon: SubIcon, label }) => (
                        <NavLink
                          key={to} to={to}
                          onClick={() => setMobileOpen(false)}
                          title={collapsed ? label : undefined}
                          style={({ isActive }) => ({
                            display: "flex", alignItems: "center", gap: 10,
                            justifyContent: collapsed ? "center" : "flex-start",
                            padding: collapsed ? "10px 0" : "10px 12px", borderRadius: "var(--radius)",
                            fontSize: 14, fontWeight: 500, marginBottom: 2,
                            color: isActive ? "var(--nav-active-text)" : "rgba(255,255,255,.55)",
                            background: isActive ? "var(--nav-active-bg)" : "transparent",
                            border: isActive ? "1px solid var(--nav-active-border)" : "1px solid transparent",
                            transition: "all .15s", textDecoration: "none",
                          })}
                        >
                          {({ isActive }) => (
                            <>
                              <SubIcon size={16} style={{ flexShrink: 0 }}/>
                              {!collapsed && (
                                <>
                                  {label}
                                  {isActive && <div style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: "var(--nav-active-text)", boxShadow: "0 0 8px var(--accent-glow)" }}/>}
                                </>
                              )}
                            </>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const { to, icon: Icon, label, end } = item;
            return (
            <NavLink
              key={to} to={to} end={end}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? label : undefined}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: 10,
                justifyContent: collapsed ? "center" : "flex-start",
                padding: collapsed ? "10px 0" : "10px 12px", borderRadius: "var(--radius)",
                fontSize: 14, fontWeight: 500, marginBottom: 2,
                color: isActive ? "var(--nav-active-text)" : "rgba(255,255,255,.55)",
                background: isActive ? "var(--nav-active-bg)" : "transparent",
                border: isActive ? "1px solid var(--nav-active-border)" : "1px solid transparent",
                transition: "all .15s", textDecoration: "none",
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon size={16} style={{ flexShrink: 0 }}/>
                  {!collapsed && (
                    <>
                      {label}
                      {isActive && <div style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: "var(--nav-active-text)", boxShadow: "0 0 8px var(--accent-glow)" }}/>}
                    </>
                  )}
                </>
              )}
            </NavLink>
            );
          })}
          {businessProfile?.superAdmin && (
            <NavLink
              to="/admin/team"
              onClick={() => setMobileOpen(false)}
              title={collapsed ? "Team" : undefined}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: 10,
                justifyContent: collapsed ? "center" : "flex-start",
                padding: collapsed ? "10px 0" : "10px 12px", borderRadius: "var(--radius)",
                fontSize: 14, fontWeight: 500, marginBottom: 2,
                color: isActive ? "var(--nav-active-text)" : "rgba(255,255,255,.55)",
                background: isActive ? "var(--nav-active-bg)" : "transparent",
                border: isActive ? "1px solid var(--nav-active-border)" : "1px solid transparent",
                transition: "all .15s", textDecoration: "none",
              })}
            >
              {({ isActive }) => (
                <>
                  <UserCog size={16} style={{ flexShrink: 0 }}/>
                  {!collapsed && (
                    <>
                      Team
                      {isActive && <div style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: "var(--nav-active-text)", boxShadow: "0 0 8px var(--accent-glow)" }}/>}
                    </>
                  )}
                </>
              )}
            </NavLink>
          )}
        </nav>

        {/* Bottom */}
        <div style={{ padding: collapsed ? "10px 8px" : "10px 12px", borderTop: "1px solid rgba(255,255,255,.08)" }}>
          <NavLink to="/admin/profile" onClick={() => setMobileOpen(false)} title={collapsed ? "Your profile" : undefined} style={{
            display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "6px 0" : "6px 4px", marginBottom: 8, textDecoration: "none", borderRadius: "var(--radius)",
          }}>
            {businessProfile?.photoURL ? (
              <img src={businessProfile.photoURL} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 34, height: 34, borderRadius: "50%", background: "var(--accent)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0,
              }}>
                {(businessProfile?.name || businessProfile?.email || "?")[0]?.toUpperCase()}
              </div>
            )}
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,.9)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {businessProfile?.name || businessProfile?.email || "—"}
                </p>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,.4)" }}>
                  {businessProfile?.superAdmin ? "Super Admin" : "Team member"}
                </p>
              </div>
            )}
          </NavLink>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "8px" : "8px 12px", marginBottom: 6,
            background: "rgba(16,185,129,.12)", border: "1px solid rgba(16,185,129,.2)", borderRadius: "var(--radius)",
          }} title={collapsed ? "System Active" : undefined}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)", animation: "pulse 2s ease infinite", flexShrink: 0 }}/>
            {!collapsed && <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 600 }}>System Active</span>}
            {!collapsed && <Zap size={11} color="var(--green)" style={{ marginLeft: "auto" }}/>}
          </div>
          <button
            onClick={handleLogout}
            title={collapsed ? "Sign out" : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              justifyContent: collapsed ? "center" : "flex-start",
              padding: collapsed ? "9px 0" : "9px 12px", background: "none", border: "1px solid transparent",
              borderRadius: "var(--radius)", color: "rgba(255,255,255,.35)",
              fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "var(--red-lt)"; e.currentTarget.style.color = "var(--red)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "rgba(255,255,255,.35)"; }}
          >
            <LogOut size={15}/> {!collapsed && "Sign out"}
          </button>
          <p style={{ textAlign: "center", fontSize: 10, color: "rgba(255,255,255,.25)", marginTop: 8, letterSpacing: ".03em" }}>
            {collapsed ? `v${APP_VERSION}` : `TechBugz v${APP_VERSION}`}
          </p>
        </div>
      </aside>

      {mobileOpen && (
        <div className="rb-sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main content */}
      <main className="rb-main" style={{
        flex: 1,
        marginLeft: collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)",
        minHeight: "100vh", overflowY: "auto", transition: "margin-left .2s ease",
      }}>
        <div className="rb-mobile-topbar">
          <button
            onClick={() => setMobileOpen(true)}
            style={{ background: "none", border: "none", color: "var(--text-1)", cursor: "pointer", display: "flex", padding: 4 }}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>Admin Panel</span>
        </div>
        <Outlet />
        <FloatingTimer />
      </main>
    </div>
  );
}